import React from "react";
import { Link } from "react-router-dom";
import { Building2, Target, Compass, CheckCircle2, MapPin, Mail, Phone, ShieldCheck, FileText, XCircle, Package, Heart, Home, Layers, Droplets, Zap, HardHat, Wrench, Quote, Globe2 } from "lucide-react";
import LeadCaptureForm from "@/components/LeadCaptureForm";
import { useI18n } from "@/lib/i18n";
import SEO from "@/components/SEO";

const CORPORATE_IMG = "https://customer-assets.emergentagent.com/job_custom-design-lab-1/artifacts/0zqydzx7_corporate-office.jpeg";

function PageHeader({ eyebrow, title, sub }) {
  return (
    <div className="bg-gradient-to-b from-emerald-50/40 to-white border-b border-slate-200">
      <div className="nc-container py-12 md:py-16 max-w-4xl">
        {eyebrow && <span className="nc-chip">{eyebrow}</span>}
        <h1 className="mt-4 text-3xl md:text-5xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: "Poppins" }}>{title}</h1>
        {sub && <p className="mt-4 text-slate-600 text-lg leading-relaxed max-w-3xl">{sub}</p>}
      </div>
    </div>
  );
}

function H2({ children }) {
  return <h2 className="mt-12 text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Poppins" }}>{children}</h2>;
}
function H3({ children }) {
  return <h3 className="mt-6 text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{children}</h3>;
}
function P({ children }) {
  return <p className="mt-3 text-slate-600 leading-relaxed">{children}</p>;
}

export function About() {
  const { t } = useI18n();
  return (
    <div data-testid="about-page">
      <SEO
        title="About Us"
        description="NakshaCart is a brand of MECHACIV Construction Pvt Ltd, serving customers across India from offices in Patna, Raipur and Noida."
        path="/about"
      />
      <PageHeader
        eyebrow={t("about.eyebrow")}
        title={t("about.title")}
        sub={t("about.sub")}
      />

      <div className="nc-container py-12 max-w-4xl">
        <P>{t("about.intro")}</P>

        {/* Vision + Mission */}
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-6 nc-soft-shadow">
            <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Compass className="w-5 h-5" /></div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("about.vision")}</h3>
            <p className="mt-2 text-slate-600 leading-relaxed">{t("about.visionD")}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6 nc-soft-shadow">
            <div className="w-11 h-11 rounded-xl bg-sky-100 text-sky-700 flex items-center justify-center"><Target className="w-5 h-5" /></div>
            <h3 className="mt-4 text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{t("about.mission")}</h3>
            <ul className="mt-3 space-y-1.5 text-slate-600 text-sm">
              {["about.missionP1", "about.missionP2", "about.missionP3", "about.missionP4"].map((k) => (
                <li key={k} className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> {t(k)}</li>
              ))}
            </ul>
          </div>
        </div>

        {/* Director */}
        <H2>{t("about.directorH")}</H2>
        <div className="mt-6 bg-white rounded-2xl border border-slate-200 nc-soft-shadow overflow-hidden" data-testid="director-section">
          <div className="p-6 md:p-8">
            <div className="flex flex-col sm:flex-row items-start gap-5">
              <img
                src="/anil-sahani.png"
                alt="Anil Kumar Sahani, Director of NakshaCart"
                className="w-28 h-28 sm:w-32 sm:h-32 rounded-2xl object-cover border border-slate-200 nc-soft-shadow flex-shrink-0"
                loading="lazy"
                data-testid="director-photo"
              />
              <div className="flex-1 min-w-0">
                <h3 className="text-xl md:text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
                  {t("about.directorName")}
                </h3>
                <p className="text-sm font-medium text-emerald-700 mt-0.5">{t("about.directorRole")}</p>
                <p className="mt-3 text-slate-700 leading-relaxed">{t("about.directorBio")}</p>
              </div>
            </div>
            <div className="mt-4 flex items-start gap-3 p-4 rounded-xl bg-sky-50/60 border border-sky-100">
              <Globe2 className="w-5 h-5 text-sky-700 flex-shrink-0 mt-0.5" />
              <p className="text-slate-700 leading-relaxed text-sm">{t("about.directorIntl")}</p>
            </div>

            <div className="mt-6 grid md:grid-cols-2 gap-5">
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-5">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Poppins" }}>
                  <Compass className="w-4 h-4 text-emerald-700" /> {t("about.directorVisionH")}
                </h4>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">{t("about.directorVisionD")}</p>
                <p className="mt-2 text-sm text-slate-700 leading-relaxed">{t("about.directorVisionD2")}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50/60 p-5">
                <h4 className="font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Poppins" }}>
                  <ShieldCheck className="w-4 h-4 text-emerald-700" /> {t("about.directorTrustH")}
                </h4>
                <p className="mt-2 text-xs text-slate-600">{t("about.directorTrustIntro")}</p>
                <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                  {["about.directorT1", "about.directorT2", "about.directorT3", "about.directorT4", "about.directorT5"].map((k) => (
                    <li key={k} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 flex-shrink-0 mt-0.5" /> {t(k)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 text-white p-5 md:p-6">
              <div className="flex items-start gap-3">
                <Quote className="w-6 h-6 flex-shrink-0 opacity-80" />
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-emerald-100">
                    {t("about.directorMsgH")}
                  </div>
                  <p className="mt-2 text-base md:text-lg leading-relaxed italic">
                    "{t("about.directorMsg")}"
                  </p>
                  <p className="mt-3 text-sm font-semibold">— {t("about.directorName")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Company info */}
        <H2>{t("about.companyInfo")}</H2>
        <div className="mt-6 grid sm:grid-cols-2 gap-5">
          <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("about.brandName")}</div>
            <div className="mt-1 font-semibold text-slate-900 text-lg">NakshaCart</div>
          </div>
          <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200">
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("about.registeredCompany")}</div>
            <div className="mt-1 font-semibold text-slate-900 text-lg">MECHACIV CONSTRUCTION PRIVATE LIMITED</div>
          </div>
        </div>

        {/* Corporate office with image */}
        <H2>{t("about.corporateOffice")}</H2>
        <div className="mt-6 grid lg:grid-cols-5 gap-6 items-center">
          <div className="lg:col-span-3 rounded-2xl overflow-hidden border border-slate-200 nc-soft-shadow">
            <img
              src={CORPORATE_IMG}
              alt="NakshaCart Corporate Office in Greater Noida"
              className="w-full h-full object-cover aspect-[16/10]"
              loading="lazy"
              data-testid="corporate-office-image"
            />
          </div>
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6">
            <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Building2 className="w-5 h-5" /></div>
            <div className="mt-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">{t("about.address")}</div>
            <p className="mt-1.5 text-slate-800 leading-relaxed">{t("about.corporateAddr")}</p>
          </div>
        </div>

        {/* Registered + branch */}
        <div className="mt-10 grid md:grid-cols-2 gap-5">
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <MapPin className="w-4 h-4" /> {t("about.registeredOffice")}
            </div>
            <p className="mt-3 text-slate-800 leading-relaxed">{t("about.registeredAddr")}</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
              <MapPin className="w-4 h-4" /> {t("about.branchOffice")}
            </div>
            <p className="mt-3 text-slate-800 leading-relaxed">{t("about.branchAddr")}</p>
          </div>
        </div>

        {/* Why choose us */}
        <H2>{t("about.whyChoose")}</H2>
        <ul className="mt-5 grid sm:grid-cols-2 gap-3">
          {["about.whyP1", "about.whyP2", "about.whyP3", "about.whyP4", "about.whyP5"].map((k) => (
            <li key={k} className="flex items-start gap-3 p-4 rounded-xl bg-gradient-to-br from-emerald-50/60 to-sky-50/40 border border-emerald-100">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
              <span className="text-slate-800 font-medium">{t(k)}</span>
            </li>
          ))}
        </ul>

        {/* Commitment */}
        <H2>{t("about.commitment")}</H2>
        <P>{t("about.commitmentD")}</P>

        {/* CTA */}
        <div className="mt-12 rounded-3xl nc-gradient p-8 md:p-10 text-white">
          <div className="flex items-start gap-3">
            <Heart className="w-6 h-6 flex-shrink-0" />
            <div>
              <h3 className="text-2xl font-semibold" style={{ fontFamily: "Poppins" }}>{t("about.contactUs")}</h3>
              <p className="mt-2 text-emerald-50 leading-relaxed max-w-xl">{t("about.contactUsD")}</p>
              <div className="mt-5 flex flex-wrap gap-3">
                <Link to="/contact" className="bg-white text-emerald-700 font-semibold rounded-lg px-5 py-2.5 text-sm hover:bg-emerald-50 transition" data-testid="about-contact-btn">
                  <Mail className="w-4 h-4 inline mr-1.5" /> {t("about.contactBtn")}
                </Link>
                <Link to="/customize" className="border-2 border-white text-white font-semibold rounded-lg px-5 py-2.5 text-sm hover:bg-white/10 transition" data-testid="about-customize-btn">
                  <Phone className="w-4 h-4 inline mr-1.5" /> {t("about.talkBtn")}
                </Link>
              </div>
              <p className="mt-5 text-xs text-emerald-50/80">
                {t("about.brandFooter")} <span className="font-semibold text-white">MECHACIV CONSTRUCTION PRIVATE LIMITED</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const SERVICE_DEFS = [
  { icon: Home,    num: "01", k: "s1", color: "bg-emerald-100 text-emerald-700" },
  { icon: Layers,  num: "02", k: "s2", color: "bg-sky-100 text-sky-700" },
  { icon: Droplets, num: "03", k: "s3", color: "bg-blue-100 text-blue-700" },
  { icon: Zap,     num: "04", k: "s4", color: "bg-amber-100 text-amber-700" },
  { icon: HardHat, num: "05", k: "s5", color: "bg-orange-100 text-orange-700" },
  { icon: Wrench,  num: "06", k: "s6", color: "bg-violet-100 text-violet-700" },
];

export function Services() {
  const { t } = useI18n();
  return (
    <div data-testid="services-page">
      <SEO
        title="Our Services"
        description="Architectural design, structural engineering, BOQ, cost estimation and end-to-end construction services across India."
        path="/services"
      />
      <PageHeader
        eyebrow={t("services.eyebrow")}
        title={t("services.title")}
        sub={t("services.sub")}
      />

      <div className="nc-container py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICE_DEFS.map((s) => (
            <div key={s.num} className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-emerald-300 hover:shadow-md transition flex flex-col">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.color}`}>
                  <s.icon className="w-6 h-6" />
                </div>
                <span className="text-3xl font-bold text-slate-100" style={{ fontFamily: "Poppins" }}>{s.num}</span>
              </div>
              <h3 className="font-semibold text-slate-900 text-lg leading-snug" style={{ fontFamily: "Poppins" }}>{t(`services.${s.k}Title`)}</h3>
              <p className="text-xs font-medium text-emerald-700 mt-0.5 mb-3">{t(`services.${s.k}Sub`)}</p>
              <p className="text-sm text-slate-600 leading-relaxed flex-1">{t(`services.${s.k}Desc`)}</p>
              <ul className="mt-4 space-y-1.5">
                {[1, 2, 3, 4].map((i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-slate-700">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" /> {t(`services.${s.k}P${i}`)}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* CTA / Lead form */}
      <div className="bg-gradient-to-b from-white to-emerald-50/30 border-t border-slate-100">
        <div className="nc-container py-14 grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <span className="nc-chip">{t("services.ctaChip")}</span>
            <h2 className="mt-4 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900">{t("services.ctaTitle")}</h2>
            <p className="mt-4 text-slate-600 text-lg leading-relaxed">{t("services.ctaSub")}</p>
            <ul className="mt-6 space-y-3">
              {["services.ctaP1", "services.ctaP2", "services.ctaP3"].map((k) => (
                <li key={k} className="flex items-center gap-2 text-sm text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0" /> {t(k)}
                </li>
              ))}
            </ul>
          </div>
          <LeadCaptureForm source="services_page" dataTestidPrefix="services-lead" />
        </div>
      </div>
    </div>
  );
}

export function Terms() {
  return (
    <div data-testid="terms-page">
      <SEO title="Terms of Service" description="The terms governing your use of NakshaCart's plans, files and services." path="/terms" />
      <PageHeader
        eyebrow="Legal"
        title="Terms of Service"
        sub="Last updated: April 2026. By using NakshaCart, you agree to the following terms."
      />
      <div className="nc-container py-10 max-w-3xl pb-16">
        <H3>1. Acceptance of Terms</H3>
        <P>By accessing or using NakshaCart (operated by MECHACIV CONSTRUCTION PRIVATE LIMITED), you agree to be bound by these Terms. If you do not agree, please discontinue use.</P>

        <H3>2. Use of Plans and Files</H3>
        <P>All plans, BOQs, cost estimates and deliverables are licensed for personal, non-commercial use. You may not redistribute, resell, or publish the watermarked PDFs on any third-party platform.</P>

        <H3>3. Account</H3>
        <P>You are responsible for keeping your login credentials safe. We are not liable for unauthorized access caused by your negligence.</P>

        <H3>4. Payments</H3>
        <P>All payments are processed securely via Razorpay. Once a paid tier is activated, downloads are metered against your allotted bundle count and validity window.</P>

        <H3>5. Partner Services</H3>
        <P>NakshaCart facilitates introductions to third-party partners (architects, engineers, contractors). NakshaCart is not a party to contracts signed directly between you and a partner.</P>

        <H3>6. Limitation of Liability</H3>
        <P>NakshaCart and MECHACIV CONSTRUCTION PRIVATE LIMITED shall not be liable for indirect, incidental or consequential damages arising from use of the service.</P>

        <H3>7. Governing Law</H3>
        <P>These Terms are governed by the laws of India. Disputes shall be subject to the exclusive jurisdiction of courts in Patna, Bihar.</P>

        <div className="mt-10 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-600">
          Questions? Email <a href="mailto:support@nakshacart.com" className="text-emerald-700 font-medium">support@nakshacart.com</a>
        </div>
      </div>
    </div>
  );
}

export function Privacy() {
  return (
    <div data-testid="privacy-page">
      <SEO title="Privacy Policy" description="How NakshaCart collects, uses and protects your personal information." path="/privacy" />
      <PageHeader
        eyebrow="Legal"
        title="Privacy Policy"
        sub="Last updated: April 2026. Your privacy matters. Here is exactly what we collect, why and how we keep it safe."
      />
      <div className="nc-container py-10 max-w-3xl pb-16">
        <P>
          MECHACIV CONSTRUCTION PRIVATE LIMITED ("we", "us", "NakshaCart") respects your privacy. This policy explains how we collect, use and protect your personal information when you use our website, mobile web app and services.
        </P>

        <H3>1. Information We Collect</H3>
        <ul className="list-disc pl-5 mt-3 text-slate-600 space-y-1.5">
          <li><strong>Account data</strong>: name, mobile number, and optionally email.</li>
          <li><strong>Transactional data</strong>: plan downloads, orders, watermark IDs, IP and device fingerprint — used for fraud prevention and quota enforcement.</li>
          <li><strong>Lead data</strong>: the plot details, location and message you submit via lead-capture forms.</li>
          <li><strong>Payment data</strong>: processed by Razorpay. We never store raw card or UPI credentials.</li>
        </ul>

        <H3>2. How We Use Your Information</H3>
        <ul className="list-disc pl-5 mt-3 text-slate-600 space-y-1.5">
          <li>To deliver the plans, BOQs and cost estimates you request.</li>
          <li>To connect you with verified NakshaCart partners near you (only when you request a consultation).</li>
          <li>For transactional communication — OTPs, download confirmations, invoices, plan-expiry reminders.</li>
          <li>To detect misuse of free-tier downloads (device fingerprinting + IP tracking).</li>
        </ul>

        <H3>3. Data Sharing</H3>
        <P>We share your contact details with a NakshaCart-verified partner <strong>only after you explicitly request a consultation</strong>. We never sell your data or share it with advertisers.</P>

        <H3>4. Cookies & Storage</H3>
        <P>We use httpOnly cookies for authentication (JWT session tokens) and <code>localStorage</code> for language preference. We do not use tracking cookies.</P>

        <H3>5. Data Retention</H3>
        <P>Account and transaction data are retained for as long as your account is active plus 7 years (as required by Indian tax & accounting law). You may request deletion by writing to <a href="mailto:privacy@nakshacart.com" className="text-emerald-700 font-medium">privacy@nakshacart.com</a>.</P>

        <H3>6. Security</H3>
        <P>All traffic is served over HTTPS. Passwords are hashed with bcrypt. Database access is restricted by network ACLs. We perform regular backups and security reviews.</P>

        <H3>7. Children's Privacy</H3>
        <P>NakshaCart is not directed to users under 18. We do not knowingly collect data from minors.</P>

        <H3>8. Your Rights</H3>
        <P>You can access, correct or delete your personal data anytime from your dashboard, or by emailing privacy@nakshacart.com. You can also opt out of non-transactional communication.</P>

        <H3>9. Contact the Data Controller</H3>
        <div className="mt-3 p-5 rounded-2xl bg-slate-50 border border-slate-200 text-sm text-slate-700 leading-relaxed">
          <strong>MECHACIV CONSTRUCTION PRIVATE LIMITED</strong><br />
          Registered Office: HN-502/3A, CN-50B, AT-JAKARIYAPUR, PO-BARI PAHARI, 2nd Street East of Bajrangi Chowk, Patna-800007<br />
          Email: <a href="mailto:privacy@nakshacart.com" className="text-emerald-700 font-medium">privacy@nakshacart.com</a>
        </div>
      </div>
    </div>
  );
}

export function Refund() {
  return (
    <div data-testid="refund-page">
      <SEO title="Refund Policy" description="NakshaCart's refund policy for digital plans, BOQs and cost estimates." path="/refund" />
      <PageHeader
        eyebrow="Legal"
        title="Refund Policy"
        sub="Last updated: April 2026."
      />
      <div className="nc-container py-10 max-w-3xl pb-16">
        <div className="p-6 rounded-2xl bg-rose-50 border border-rose-200 flex items-start gap-3">
          <XCircle className="w-6 h-6 text-rose-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-rose-900" style={{ fontFamily: "Poppins" }}>No refunds applicable</h3>
            <p className="mt-1 text-sm text-rose-800 leading-relaxed">
              All purchases on NakshaCart are final. No refunds are issued once a paid plan has been activated or after any bundle has been downloaded, whether used or unused.
            </p>
          </div>
        </div>

        <H3>Why are purchases non-refundable?</H3>
        <P>
          NakshaCart delivers digital goods — house plans, BOQs, cost-estimates and sample deliverables. Because these documents are downloaded and can be stored by the customer immediately, they cannot be "returned". Unused bundles within a paid plan do not carry over beyond the validity window and are not refunded.
        </P>

        <H3>Exceptions</H3>
        <P>
          If a duplicate charge has been made due to a payment-gateway error, please write to <a href="mailto:billing@nakshacart.com" className="text-emerald-700 font-medium">billing@nakshacart.com</a> within 7 days of the charge. After verification, the duplicate amount will be refunded to the original payment method within 7–10 working days.
        </P>

        <H3>Free Tier</H3>
        <P>
          Every registered mobile gets 2 free bundle downloads (lifetime). We recommend using these free bundles to evaluate NakshaCart before committing to a paid plan.
        </P>

        <H3>Contact</H3>
        <P>For billing queries: <a href="mailto:billing@nakshacart.com" className="text-emerald-700 font-medium">billing@nakshacart.com</a></P>
      </div>
    </div>
  );
}

export function Cancellation() {
  return (
    <div data-testid="cancellation-page">
      <SEO title="Cancellation Policy" description="How NakshaCart subscriptions and bundle credits work — no auto-renewal, no cancellations." path="/cancellation" />
      <PageHeader
        eyebrow="Legal"
        title="Cancellation Policy"
        sub="Last updated: April 2026."
      />
      <div className="nc-container py-10 max-w-3xl pb-16">
        <div className="p-6 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3">
          <XCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-amber-900" style={{ fontFamily: "Poppins" }}>Subscriptions cannot be cancelled</h3>
            <p className="mt-1 text-sm text-amber-900 leading-relaxed">
              Once a NakshaCart subscription (Basic / Standard / Premium) has been purchased, it cannot be cancelled or converted into another tier. You may use the remaining bundles until the validity window expires.
            </p>
          </div>
        </div>

        <H3>How subscriptions work</H3>
        <ul className="list-disc pl-5 mt-3 text-slate-600 space-y-1.5">
          <li>Each tier grants a fixed number of plan bundles and a fixed validity window (see <Link to="/pricing" className="text-emerald-700 font-medium">pricing</Link>).</li>
          <li>Bundles are consumed as unique plans are downloaded. Re-downloading the same bundle does not consume additional credits.</li>
          <li>At the end of the validity period, any unused bundles expire and are <strong>not</strong> carried forward, credited back, or refunded.</li>
          <li>A new subscription can only be activated after the current one has expired — NakshaCart does not allow plan stacking.</li>
        </ul>

        <H3>No auto-renewal</H3>
        <P>
          NakshaCart subscriptions are one-time purchases. They do <strong>not</strong> auto-renew. You will never be charged again unless you manually purchase a new plan.
        </P>

        <H3>Abandoning a consultation</H3>
        <P>
          If you have submitted a consultation lead via a "Talk to Expert" form, you may ask us to stop partner outreach anytime by emailing <a href="mailto:support@nakshacart.com" className="text-emerald-700 font-medium">support@nakshacart.com</a>. This does not affect any paid subscription you may hold.
        </P>

        <H3>Contact</H3>
        <P>For subscription queries: <a href="mailto:support@nakshacart.com" className="text-emerald-700 font-medium">support@nakshacart.com</a></P>
      </div>
    </div>
  );
}

export function Delivery() {
  return (
    <div data-testid="delivery-page">
      <SEO title="Delivery Policy" description="NakshaCart delivers plans, BOQs and cost estimates digitally, instantly after payment." path="/delivery" />
      <PageHeader
        eyebrow="Legal"
        title="Delivery Policy"
        sub="Last updated: April 2026."
      />
      <div className="nc-container py-10 max-w-3xl pb-16">
        <div className="p-6 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-start gap-3">
          <Package className="w-6 h-6 text-emerald-600 flex-shrink-0" />
          <div>
            <h3 className="font-semibold text-emerald-900" style={{ fontFamily: "Poppins" }}>Digital delivery only</h3>
            <p className="mt-1 text-sm text-emerald-900 leading-relaxed">
              NakshaCart delivers all products digitally. No physical goods are shipped. All documents are delivered as watermarked PDF downloads available directly from your account dashboard.
            </p>
          </div>
        </div>

        <H3>What you get after payment</H3>
        <ul className="list-disc pl-5 mt-3 text-slate-600 space-y-1.5">
          <li>Instant activation of the purchased tier — the bundle count and validity appear in your dashboard immediately.</li>
          <li>Access to download any matching plan bundle — each containing <strong>Floor Plan + BOQ + Cost Estimate + Sample Deliverables</strong> as separate PDFs.</li>
          <li>Every PDF is uniquely watermarked with your name, mobile, timestamp and a download ID.</li>
        </ul>

        <H3>Delivery timeline</H3>
        <P>
          Digital downloads are available <strong>immediately</strong> after a successful payment. Order confirmation and invoice (if email provided) are sent via email within 15 minutes.
        </P>

        <H3>Consultation delivery</H3>
        <P>
          When you submit a "Talk to Expert" lead, NakshaCart connects you with a verified partner within 24 working hours. The partner then directly contacts you via phone / WhatsApp / email.
        </P>

        <H3>Download issues</H3>
        <P>
          If a download fails or the PDF is corrupted, simply re-download it — re-downloads do <strong>not</strong> consume additional bundle credits. Still stuck? Write to <a href="mailto:support@nakshacart.com" className="text-emerald-700 font-medium">support@nakshacart.com</a> with your order ID and we'll fix it within 1 working day.
        </P>

        <H3>Contact</H3>
        <P>For delivery queries: <a href="mailto:support@nakshacart.com" className="text-emerald-700 font-medium">support@nakshacart.com</a></P>
      </div>
    </div>
  );
}

export function Legal() {
  const docs = [
    { slug: "terms", title: "Terms of Service", sub: "The rules for using NakshaCart — licensing of plans, account safety and dispute jurisdiction.", icon: FileText, color: "bg-slate-100 text-slate-700" },
    { slug: "privacy", title: "Privacy Policy", sub: "What data we collect, how we use it, how we keep it safe — and your rights as a data subject.", icon: ShieldCheck, color: "bg-emerald-100 text-emerald-700" },
    { slug: "refund", title: "Refund Policy", sub: "No refunds on digital goods. Duplicate-charge exception and billing contact.", icon: XCircle, color: "bg-rose-100 text-rose-700" },
    { slug: "cancellation", title: "Cancellation Policy", sub: "Subscriptions cannot be cancelled. Bundle mechanics and no auto-renewal.", icon: XCircle, color: "bg-amber-100 text-amber-700" },
    { slug: "delivery", title: "Delivery Policy", sub: "Digital delivery only. Instant activation, what's in each bundle, download-failure path.", icon: Package, color: "bg-sky-100 text-sky-700" },
  ];
  return (
    <div data-testid="legal-page">
      <SEO title="Policies & Legal Documents" description="Terms, Privacy, Refund, Cancellation and Delivery policies for NakshaCart in one place." path="/legal" />
      <PageHeader
        eyebrow="Legal Hub"
        title="Policies & Legal Documents"
        sub="Everything you need to know about using NakshaCart — Terms, Privacy, Refund, Cancellation and Delivery policies in one place."
      />
      <div className="nc-container py-10 max-w-5xl pb-16">
        <div className="grid md:grid-cols-2 gap-5">
          {docs.map((d) => (
            <Link
              key={d.slug}
              to={`/${d.slug}`}
              className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-emerald-300 transition group nc-soft-shadow"
              data-testid={`legal-card-${d.slug}`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${d.color} flex-shrink-0`}>
                  <d.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-slate-900 group-hover:text-emerald-700 transition" style={{ fontFamily: "Poppins" }}>{d.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-600 leading-relaxed">{d.sub}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700">
                    Read full policy →
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-10 p-6 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-start sm:items-center gap-4">
          <div className="w-11 h-11 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
            <Mail className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>Need clarification?</h3>
            <p className="mt-1 text-sm text-slate-600 leading-relaxed">
              Legal / compliance queries: <a href="mailto:privacy@nakshacart.com" className="text-emerald-700 font-medium">privacy@nakshacart.com</a>
              {" · "}Billing: <a href="mailto:billing@nakshacart.com" className="text-emerald-700 font-medium">billing@nakshacart.com</a>
              {" · "}General support: <a href="mailto:support@nakshacart.com" className="text-emerald-700 font-medium">support@nakshacart.com</a>
            </p>
            <p className="mt-3 text-xs text-slate-500">
              NakshaCart is a brand of <strong>MECHACIV CONSTRUCTION PRIVATE LIMITED</strong>. Registered Office: HN-502/3A, CN-50B, Jakariyapur, Patna-800007.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export function NotFound() {
  return (
    <div className="nc-container py-20 text-center" data-testid="not-found-page">
      <SEO title="Page Not Found" noindex />
      <h1 className="text-6xl font-semibold text-slate-900">404</h1>
      <p className="mt-3 text-slate-500">Page not found.</p>
      <Link to="/" className="mt-6 inline-block nc-btn-primary">Go home</Link>
    </div>
  );
}
